import {
    ErrorCode,
    ZAPIError,
    isHttp50XError,
} from '../../helpers/error/index.js';

const RETRY_TIMEOUT = 3000;

/**
 * Creates promise which resolves automatically after the specified amount of time.
 *
 * @param {number} timeout
 * @returns {Promise<void>}
 */
const delayedResolve = (timeout) => {
    return new Promise((resolve) => {
        setTimeout(resolve, timeout);
    });
};

/**
 * Attempts to extract an internal error code from provided ZAPI response.
 *
 * @param {Promise<any>} json
 * @returns {Promise<$ZAPI.Response.ErrorResponse>}
 */
const getError = (json) => {
    return json.catch(() => {
        return {
            success: false,
            internal_code: ErrorCode.UNKNOWN,
            http_status: null,
        };
    });
};

/**
 * @param {HttpRequestInfo} request
 * @param {boolean} [retryOn50X]
 * @returns {Promise<object>}
 */
export const sendRequest = async (request, retryOn50X) => {
    let response;

    /**
     * @type {RequestInit}
     */
    const fetchOptions = request.httpOptions;

    try {
        // Make a request to ZAPI.
        response = await fetch(request.path, fetchOptions);
    } catch (e) {
        const zapiError = new ZAPIError({
            code: ErrorCode.NETWORK_FAILURE,
            request,
            response: null,
            status: null,
        });

        // Request resulted in a network failure. No need to proceed.
        return Promise.reject(zapiError);
    }

    // No matter what the response status is, we need JSON data as it might contain
    // an internal code which would tell us why request was rejected by ZAPI.
    //
    // At this point there is no need to wait for JSON to be parsed.
    const result = response.json();

    if (!response.ok) {
        const {status} = response;

        // Lets make another attempt as it might be just a random server error.
        if (isHttp50XError(status) && retryOn50X) {
            await delayedResolve(RETRY_TIMEOUT);

            return sendRequest(request);
        }

        // Seems that the error isn't random. Try to extract an internal code.
        const error = await getError(result);

        const zapiError = new ZAPIError({
            code: error.internal_code,
            response: error,
            request,
            status,
        });

        return Promise.reject(zapiError);
    }

    try {
        // Wait for JSON to be parsed.
        return await result;
    } catch (e) {
        const zapiError = new ZAPIError({
            code: ErrorCode.INVALID_JSON,
            status: response.status,
            response: null,
            request,
        });

        // Even though the request's was successful it returned an invalid JSON.
        return Promise.reject(zapiError);
    }
};

/**
 * @typedef {import('./interfaces').HttpRequestInfo} HttpRequestInfo
 */
